import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { fg } from '@confluence/feature-gating';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';

import type {
	useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery$data,
	useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery$variables,
} from './__generated__/useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery.graphql.ts';

import { useIsEligibleForNthUserHomeOnboardingLicensedUserCount } from './useIsEligibleForNthUserHomeOnboardingLicensedUserCount.ts';

const useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery = graphql`
	query useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery {
		user: confluence_atlassianUser(current: true) {
			id
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

export interface NthUserRevampHookReturn {
	isEligible: boolean;
	isVisible: boolean;
	loading: boolean;
	error?: Error;
}

/**
 * Custom hook to determine eligibility for nth user home onboarding revamp experiment
 *
 * Conditions includes:
 * 1. Feature gate: cc-enable-nth-user-home-onboarding-revamp must be enabled
 * 2. Whether the user is licensed (site admin or nth user)
 * 3. Whether the user is an nth user (not founding admin)
 * 4. Whether the user is on an instance with over ten users
 * 5. Whether the experience is not seen or seen within 14 days
 * 6. Whether the user is new (joined within the last 15 days)
 *
 */
export const useIsEligibleForNthUserHomeOnboardingRevamp = (): NthUserRevampHookReturn => {
	const { accessStatus } = useSessionData();

	const isLicensedUser =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values({
			IS_FOUNDING_ADMIN: ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
		}),
		!isLicensedUser || !fg('cc-enable-nth-user-home-onboarding-revamp'),
	);

	const { isFoundingAdmin, takeoverSeenAt } = deserializeState(onboardingStateData);

	const isNthUser =
		isLicensedUser && !onboardingStateLoading && !onboardingStateError && !isFoundingAdmin;

	const {
		licensedUserCount,
		loading: userCountDataLoading,
		error: userCountDataError,
	} = useIsEligibleForNthUserHomeOnboardingLicensedUserCount(
		!isNthUser || !fg('cc-enable-nth-user-home-onboarding-revamp'),
	);

	const { isNewUser, loading: isNewUserLoading } = useIsNewUser(
		{ activeDaysThreshold: 15 },
		!isNthUser || !fg('cc-activation-unify-home-onboarding-kill-switch'),
	);

	const isOverTenUserInstance = Boolean(licensedUserCount && licensedUserCount > 10);

	const {
		data: userData,
		loading: userDataLoading,
		error: userDataError,
	} = useQueryInterop<
		useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery$data,
		useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery$variables
	>(useIsEligibleForNthUserHomeOnboardingRevampGetUserDataQuery, {
		skip: !isNthUser || !fg('cc-enable-nth-user-home-onboarding-revamp'),
	});

	const personalSpaceKey =
		userData?.user?.confluence?.hasPersonalSpace && userData?.user?.confluence?.space?.key;

	const isExperienceWithinFourteenDays = useMemo(() => {
		if (!takeoverSeenAt) return true;
		return differenceInDays(new Date(), new Date(takeoverSeenAt)) <= 14;
	}, [takeoverSeenAt]);

	const eligibilityError = onboardingStateError || userCountDataError || userDataError;

	if (
		eligibilityError &&
		(isUnauthorizedError(eligibilityError) || isExpectedApolloError(eligibilityError))
	) {
		markErrorAsHandled(eligibilityError);
	}

	const loading =
		onboardingStateLoading || userCountDataLoading || userDataLoading || Boolean(isNewUserLoading);

	const isEligible = Boolean(
		!eligibilityError &&
		!loading &&
		isNthUser &&
		isOverTenUserInstance &&
		!!personalSpaceKey &&
		isNewUser === true,
	);

	return {
		isEligible,
		isVisible: isEligible && isExperienceWithinFourteenDays,
		loading,
		error: eligibilityError,
	};
};
